import { styled } from '@mui/material/styles';
import { InputBase, Select, ToggleButtonGroup } from '@mui/material';

/** Label inside an on-state filter toggle. The filled background stays; the underline marks the control as on. */
export const FilterToggleLabel = styled('span')({
  textDecoration: 'underline',
  textUnderlineOffset: '0.18em',
});

export const FILTER_HEIGHT = 36;

export const filterSurface = (theme) =>
  theme.palette.mode === 'dark' ? theme.palette.action.disabledBackground : theme.palette.grey[100];

export const FilterBar = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: theme.spacing(1),
  width: '100%',
  overflowX: 'auto',
  minHeight: FILTER_HEIGHT,
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  '&.flush': {
    flex: 1,
    minWidth: 0,
    padding: 0,
  },
  '&.inset': {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  '&.inset-2': {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  '@media print': {
    display: 'none',
  },
}));

export const FilterResetWrap = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  overflow: 'hidden',
  width: 0,
  // Cancel the FilterBar gap on the trailing side so a collapsed control
  // doesn't leave an 8px hole (or a double-gap) before the next item.
  marginRight: theme.spacing(-1),
  opacity: 0,
  pointerEvents: 'none',
  transition: theme.transitions.create(['width', 'margin', 'opacity'], {
    duration: theme.transitions.duration.shorter,
    easing: theme.transitions.easing.easeInOut,
  }),
  '&.open': {
    width: FILTER_HEIGHT,
    marginRight: 0,
    opacity: 1,
    pointerEvents: 'auto',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

export const FilterControl = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'stretch',
  flexShrink: 0,
  height: FILTER_HEIGHT,
  borderRadius: 4,
  overflow: 'hidden',
  backgroundColor: filterSurface(theme),
  '&.active': {
    backgroundColor: 'transparent',
  },
  '& .MuiButton-root': {
    height: FILTER_HEIGHT,
    minHeight: FILTER_HEIGHT,
    minWidth: FILTER_HEIGHT,
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    textTransform: 'none',
    whiteSpace: 'nowrap',
    fontWeight: 500,
    boxShadow: 'none',
    borderRadius: 4,
    lineHeight: 1.2,
  },
  '&.icon-only .MuiButton-root': {
    width: FILTER_HEIGHT,
    paddingLeft: 0,
    paddingRight: 0,
    justifyContent: 'center',
  },
}));

export const FilterSearchControl = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  height: FILTER_HEIGHT,
  minWidth: 200,
  borderRadius: 4,
  overflow: 'hidden',
  backgroundColor: filterSurface(theme),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(0.5),
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  '& .MuiIcon-root, & .MuiSvgIcon-root': {
    fontSize: 20,
    color: theme.palette.text.secondary,
  },
  [theme.breakpoints.down('md')]: {
    minWidth: 160,
  },
}));

export const FilterSearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  minWidth: 140,
  height: FILTER_HEIGHT,
  color: theme.palette.text.primary,
  '& .MuiInputBase-input': {
    padding: theme.spacing(0.75, 1, 0.75, 0),
    height: FILTER_HEIGHT,
    boxSizing: 'border-box',
    fontSize: '0.875rem',
  },
}));

export const FilterSelect = styled(Select)(({ theme }) => ({
  height: FILTER_HEIGHT,
  flexShrink: 0,
  borderRadius: 4,
  backgroundColor: filterSurface(theme),
  color: theme.palette.text.primary,
  '&:before, &:after': {
    display: 'none',
  },
  '&.MuiFilledInput-root, &.MuiInputBase-root': {
    backgroundColor: filterSurface(theme),
    borderRadius: 4,
    '&:hover, &.Mui-focused': {
      backgroundColor: filterSurface(theme),
    },
  },
  '&.selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '&.MuiFilledInput-root.selected, &.MuiInputBase-root.selected': {
    backgroundColor: theme.palette.primary.main,
    '&:hover, &.Mui-focused': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  '& .MuiSelect-select, & .MuiFilledInput-input': {
    padding: theme.spacing(0.75, 1.5),
    paddingRight: 32,
    height: FILTER_HEIGHT,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.875rem',
    backgroundColor: 'transparent',
  },
  '& .MuiSvgIcon-root': {
    color: theme.palette.text.secondary,
  },
  '&.selected .MuiSvgIcon-root': {
    color: theme.palette.primary.contrastText,
  },
}));

export const FilterRatingGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  height: FILTER_HEIGHT,
  flexShrink: 0,
  backgroundColor: filterSurface(theme),
  borderRadius: 4,
  '& .MuiToggleButtonGroup-grouped': {
    border: 0,
    margin: 0,
    height: FILTER_HEIGHT,
    minWidth: FILTER_HEIGHT,
    padding: theme.spacing(0, 1.25),
    textTransform: 'none',
    color: theme.palette.text.primary,
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
      '& .MuiIcon-root, & .MuiSvgIcon-root': {
        color: theme.palette.primary.contrastText,
      },
    },
  },
  '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
    borderRadius: 0,
    marginLeft: 0,
  },
  '& .MuiToggleButtonGroup-grouped:first-of-type': {
    borderRadius: '4px 0 0 4px',
  },
  '& .MuiToggleButtonGroup-grouped:last-of-type': {
    borderRadius: '0 4px 4px 0',
  },
  '& .MuiToggleButtonGroup-grouped:only-of-type': {
    borderRadius: 4,
  },
}));

export const RatingIconWrap = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  color: theme.palette.success.main,
  '&.neg': {
    color: theme.palette.error.main,
  },
  '& .MuiIcon-root': {
    fontSize: 18,
  },
}));
